import React, { Suspense, lazy, useMemo } from "react";
import "../index.scss";
import "../assets/css/custom.scss";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import styled from "styled-components";
import ModalContentV2 from "components/modal/ModalV2";
import { ReactComponent as NoInternetIcon } from "assets/icon_no_internet.svg";
import { ReactComponent as IconWarningDialog } from "assets/icon_warning_dialog.svg";
import i18n from "i18n";
import { useDispatch, useSelector } from "react-redux";
import {
  setApiErrorData,
  setApiErrorOpen,
  setOpenNoInternetModal,
} from "redux/action";
import { checkNetworkConnection } from "utils/ServiceBase";
import useClearApiErrorData from "hooks/useClearApiError";
import { HOTPOT_GUIDELINE, HOTPOT_SELECT_SOUP } from "utils/routerPath";
import CookieConsent from "utils/CookieConsent";
import BottomNavigation from "components/footers/BottomNavigation";
import TopNavigation from "components/headers/TopNavigation";
import ModalRateTip from "components/modal/ModalRateTip";

// screens (lazy)
const Home = lazy(() => import("./home"));
const HomeStatic = lazy(() => import("./qr-static"));
const HotpotGuideline = lazy(() => import("./hotpot-guideline"));
const HotpotSelectSoup = lazy(() => import("./hotpot-select-soup"));
const Cart = lazy(() => import("./cart"));
const ThankForVisiting = lazy(() => import("./thank-for-visiting"));
const CallStaff = lazy(() => import("./call-staff"));
const OrderSuccess = lazy(() => import("./order-success"));
const NotFoundLazy = lazy(() =>
  import("./error/404").then((m) => ({ default: m.NotFoundScreen })),
);
const Products = lazy(() => import("./products"));
const OrderedScreen = lazy(() => import("./ordered"));
const CheckoutScreen = lazy(() => import("./checkout"));
const Login = lazy(() => import("./login"));
const PaymentInfo = lazy(() => import("./payment"));
const AppLoginCallback = lazy(() => import("./app-login-callback"));
const BillInfo = lazy(() => import("./bill"));
const CategoryScreen = lazy(() => import("./categories"));
const SearchScreen = lazy(() => import("./search"));
const RequireQrcode = lazy(() => import("./home/require-qrcode"));
const AppInApp = lazy(() => import("./appinapp"));
const AppInAppOneMount = lazy(() => import("./onemount"));
const VerifyAppInApp = lazy(() => import("./verify-payment-appinapp"));
const VerifyAppInAppOnemount = lazy(
  () => import("./verify-payment-appinapp-onemount"),
);
const ThankForChooseMenu = lazy(() => import("./choose-menu"));
const SignIn = lazy(() => import("./sign-in"));

const BOTTOM_NAV_VISIBLE_PATHS = new Set([
  "/products",
  "/cart",
  "/ordered",
  "/checkout",
]);

const RouterShell = styled.div`
  min-height: 100%;
  box-sizing: border-box;

  /* &.has-bottom-nav {
    padding-top: calc(68px + env(safe-area-inset-top, 0px));
    padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  } */
`;

const AppRouterContent = () => {
  const location = useLocation();
  const hasBottomNav = useMemo(
    () => BOTTOM_NAV_VISIBLE_PATHS.has(location.pathname),
    [location.pathname],
  );

  return (
    <RouterShell className={hasBottomNav ? "has-bottom-nav" : ""}>
      <Suspense fallback={<div />}>
        <Routes>
          <Route path="/" element={<RequireQrcode />} />
          <Route path="/qr-order" element={<Home />} />
          <Route path="/qr-order/static" element={<HomeStatic />} />
          <Route path={HOTPOT_GUIDELINE} element={<HotpotGuideline />} />
          <Route path={HOTPOT_SELECT_SOUP} element={<HotpotSelectSoup />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/call-staff" element={<CallStaff />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/thank-for-visiting" element={<ThankForVisiting />} />
          <Route path="/choose-menu" element={<ThankForChooseMenu />} />

          <Route path="/products" element={<Products />} />
          <Route path="/login" element={<Login />} />
          <Route path="/app-login-callback" element={<AppLoginCallback />} />
          <Route path="/search" element={<SearchScreen />} />
          <Route path="/payment" element={<PaymentInfo />} />
          <Route path="/ordered" element={<OrderedScreen />} />
          <Route path="/checkout" element={<CheckoutScreen />} />
          <Route path="/bill" element={<BillInfo />} />
          <Route path="/categories" element={<CategoryScreen />} />
          <Route path="/qr-order/appinapp/:guid" element={<AppInApp />} />
          <Route
            path="/qr-order/appinapp/addPrepay/:guid/:request-guid"
            element={<AppInAppOneMount />}
          />
          <Route path="*" element={<NotFoundLazy />} />
          <Route path="/verify-payment-appinapp" element={<VerifyAppInApp />} />
          <Route
            path="/verify-payment-appinapp-onemount"
            element={<VerifyAppInAppOnemount />}
          />
          <Route path="/sign-in" element={<SignIn />} />
        </Routes>
      </Suspense>
      {/* <TopNavigation /> */}
      {/* <BottomNavigation /> */}
      <ModalRateTip />
    </RouterShell>
  );
};

const AppRouter = () => {
  const apiErrors = useSelector((state) => state.rootReducer.apiErrors);
  const isOpenModalNoInternet = useSelector(
    (state) => state.rootReducer.isOpenModalNoInternet,
  );
  const dispatch = useDispatch();
  useClearApiErrorData();

  const handleCloseModal = () => {
    dispatch(setApiErrorOpen(false));
    dispatch(setApiErrorData([]));
  };

  return (
    <Router>
      <AppRouterContent />
      <ModalContentV2
        icon={<IconWarningDialog />}
        modalIsOpen={apiErrors?.isOpen}
        title={i18n.t("apiNoResponse")}
        titleConfirm={i18n.t("close")}
        isShowBtnCancel={false}
        onConfirm={() => {
          handleCloseModal();
        }}
        content={
          <div className="api-error__api-list">
            {apiErrors?.apis?.map((api, index) => (
              <div>
                <div>
                  <b>{index + 1}.</b>
                  <div>Path: {api?.url}</div>
                </div>
              </div>
            ))}
          </div>
        }
        error={""}
      />
      {/* <CookieConsent /> */}
      <ModalContentV2
        icon={<NoInternetIcon />}
        modalIsOpen={isOpenModalNoInternet}
        title={i18n.t("textTitleNoInternet")}
        titleConfirm={i18n.t("textReload")}
        isShowBtnCancel={false}
        onConfirm={() => {
          if (checkNetworkConnection()) {
            window.location.reload();
          } else {
            setTimeout(() => dispatch(setOpenNoInternetModal(true)), 500);
          }
          dispatch(setOpenNoInternetModal(false));
        }}
        content={i18n.t("textContentNoInternet")}
        error={""}
      />
    </Router>
  );
};

export default AppRouter;
